import React from "react"; // No need to import { Component }
import { Card, Divider, Grid, Header, Icon, Table } from "semantic-ui-react"; // Ensure Icon is imported
import "../../node_modules/react-vis/dist/style.css";
import ChartUtils from "../charts/ChartUtils";
import "../css/home.css"; // Ensure you add the CSS classes here
import Utils from "../util/Utils";
import { isMobile } from "react-device-detect";

const renderTableYearHeaders = (dataSource, yearDataKey) => {
  return Object.keys(dataSource).map((index) => {
    const dataItem = dataSource[index];
    const year = dataItem?.[yearDataKey]?.year;
    if (!year) return null;
    const headerLabel = year.substring(0, 4);
    return <Table.HeaderCell key={index}>{headerLabel}</Table.HeaderCell>;
  });
};

const renderDataCells = (
  dataSource,
  dataKey,
  format_function,
  valuePath,
  params
) => {
  return Object.keys(dataSource).map((index) => {
    const cellData = dataSource[index]?.[dataKey]?.[valuePath];
    const cellElement = Utils.TableCell(cellData, format_function, ...params);
    return React.cloneElement(cellElement, { key: index });
  });
};

const moatTableRowsConfig = [
  {
    label: "Gross Margin", // Label for the first cell
    dataKey: "Gross Margin", // Key in incomeStmtValues[index]
    valuePath: "value", // Property under dataKey (.value)
    format: Utils.format_percent, // Formatting function
    params: [0.5, 1], // Additional params for Utils.TableCell (threshold, precision)
    trendChartKey: "Gross Margin", // Key for getXyDataFromValues for the tiny chart
    trendChartDataKey: "Value", // Data key for the tiny chart ("Value" or "Growth")
  },
  {
    label: "YoY Growth", // Label for the first cell
    dataKey: "Gross Margin", // Data source for growth is Gross Margin
    valuePath: "yoyGrowth", // Use yoyGrowth property
    format: Utils.format_percent,
    params: [0, 1], // Additional params for Utils.TableCell
    trendChartKey: "Gross Margin", // Trend chart is based on Gross Margin data itself
    trendChartDataKey: "Growth", // Data key for the tiny chart
  },
  {
    label: "Profit Margin", // Label for the first cell
    dataKey: "Profit Margin", // Key in incomeStmtValues[index]
    valuePath: "value", // Property under dataKey (.value)
    format: Utils.format_percent,
    params: [0.25, 1], // Additional params for Utils.TableCell (threshold, precision)
    trendChartKey: "Profit Margin", // Key for getXyDataFromValues for the tiny chart
    trendChartDataKey: "Value", // Data key for the tiny chart
  },
  {
    label: "YoY Growth", // Label for the first cell
    dataKey: "Profit Margin", // Data source for growth is Profit Margin
    valuePath: "yoyGrowth", // Use yoyGrowth property
    format: Utils.format_percent,
    params: [0, 1], // Additional params for Utils.TableCell
    trendChartKey: "Profit Margin", // Trend chart is based on Profit Margin data itself
    trendChartDataKey: "Growth", // Data key for the tiny chart
  },
];

const MoatCheck = (props) => {
  const incomeYValues = props.stockValues.incomeStmtValues.slice(
    isMobile ? -3 : -5
  );

  return (
    <>
      <Divider horizontal>
        <Header as="h4">
          <Icon name="angle double up" />
          Does it have a sustainable Competitive Advantage ?
        </Header>
      </Divider>
      <br />

      <Grid stackable centered>
        <Grid.Row columns={{ mobile: 1, tablet: 2, computer: 2 }}>
          <Grid.Column width={5} >
            <div className="chart-container" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {ChartUtils.renderYoYGrowthCard(
                "Gross Margin YoY", // Title for the growth card
                ChartUtils.getXyDataFromValues(
                  props.stockValues.incomeStmtValues, // Use income statement data
                  "Gross Margin" // Use Gross Margin data key
                ),
                Utils.format_percent // Formatting for the card value
              )}
              {ChartUtils.renderYoYGrowthCard(
                "Profit Margin YoY", // Title for the growth card
                ChartUtils.getXyDataFromValues(
                  props.stockValues.incomeStmtValues, // Use income statement data
                  "Profit Margin" // Use Profit Margin data key
                ),
                Utils.format_percent // Formatting for the card value
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={11}>
            <div style={{ padding: "0 0.5rem" }}>
              <Card fluid raised>
                <Card.Content>
                  <Card.Header textAlign="center">
                    Past Years Values
                    <Header.Subheader style={{ marginTop: "0.25em" }}>
                      (Gross Margin &gt; 50% and Profit Margin &gt; 25% is good)
                    </Header.Subheader>
                  </Card.Header>
                  <Card.Description>
                    <Table
                      size="small"
                      unstackable
                      compact="very"
                      celled
                      className="table-margin-auto moat-table-compact"
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Year</Table.HeaderCell>
                          {renderTableYearHeaders(
                            incomeYValues,
                            "Gross Margin"
                          )}
                          <Table.HeaderCell>Trend</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {moatTableRowsConfig.map((rowConfig) => (
                          <Table.Row
                            key={`${rowConfig.dataKey}-${rowConfig.valuePath}`}
                          >
                            <Table.Cell>{rowConfig.label}</Table.Cell>
                            {renderDataCells(
                              incomeYValues, // Pass the full data object
                              rowConfig.dataKey, // Pass the data key (e.g., "Gross Margin", "Profit Margin")
                              rowConfig.format, // Pass the format function
                              rowConfig.valuePath, // Pass the path to the specific value (.value or .yoyGrowth)
                              rowConfig.params // Pass additional params for Utils.TableCell
                            )}

                            {ChartUtils.renderTinyChart(
                              ChartUtils.getXyDataFromValues(
                                incomeYValues,
                                rowConfig.trendChartKey // Use the trend chart key from config
                              ),
                              rowConfig.trendChartDataKey // Use the trend chart data key from config
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  The term economic moat, refers to a business' ability to
                  maintain competitive advantages over its competitors in order
                  to protect its long-term profits and market share from
                  competing firms. Just like a medieval castle, the moat serves
                  to protect those inside the fortress and their riches from
                  outsiders.
                  <br /> <br />
                  In general, A company that has Cost advantage, Size advantage,
                  High Switching Costs, Networking Effect has a Wide Economic
                  Moat.
                  <br /> <br />
                  Looking at a company's historical Gross Margin and Profit
                  Margin tells us if the company was able to keep its stand
                  against the competitors.
                </Card.Content>
              </Card>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default MoatCheck;
